/* eslint-disable no-undef */
import React from 'react';
import i18n from 'i18n-js';
import { Route, IndexRoute } from 'react-router';
import _ from 'lodash';

import AppContainer from 'views/app/AppContainer';
import AuthorizedUserContainer from 'views/app/AuthorizedUserContainer';
import WelcomePage from 'views/app/WelcomePage';

import NotFoundPage from 'views/not-found/NotFoundPage';
import LoginPage from 'views/login/LoginPage';

import TridentCampaignViewPage from 'views/trident-campaigns/common/TridentCampaignView.tsx';
import ManageEventConditionActionPage from 'views/trident-campaigns/manage-event-condition-action/ManageEventConditionActionPage';

import modelsConfig from 'data/models-config';

import { injectModelPropsToView } from 'utils/route-utils';

import { RTC_NAMESPACE } from 'namespace';
import ProjectDetailPage from 'views/ucd/ProjectDetailPage';
import DebuggingToolsPage from 'views/debugging-tools/DebuggingToolsPage';
import ContentGenerationConfigStandalonePage from 'views/ucd/content-config/ContentGenerationConfigStandalonePage';

// @Plop: IMPORT_MODEL_FILES

function routeName(model, action = '') {
  if (action !== '') {
    return i18n.t(`breadcrumbs.${action}`, {
      model: i18n.t(`models.${model}`),
    });
  }

  return i18n.t(`breadcrumbs.models.${model}`, {
    defaults: { scope: `sidebar.${model}` },
  });
}

function getRoutes(modelConfig: any) {
  const { webPath, routes, localeSelector, views } = modelConfig;
  const v1ViewsKeys = ['index', 'search', 'new', 'show', 'edit', 'review', 'metrics'];
  const v1Routes = [
    _.has(views, 'index') && (
      <Route
        key={'index'}
        name={routeName(localeSelector)}
        path={_.get(routes, 'index') ? _.get(routes, 'index') : webPath}
        component={views.index}
      />
    ),
    _.has(views, 'search') && (
      <Route
        key={'search'}
        name={routeName(localeSelector)}
        path={_.get(routes, 'search') ? _.get(routes, 'search') : webPath}
        component={views.search}
      />
    ),
    _.has(views, 'new') && (
      <Route
        key={'create'}
        name={routeName(localeSelector, 'create')}
        path={
          _.get(routes, 'new')
            ? `${_.get(routes, 'new')}/new`
            : `${webPath}/new`
        }
        component={views.new}
      />
    ),
    _.has(views, 'show') && (
      <Route
        key={'details'}
        name={routeName(localeSelector, 'details')}
        staticName
        path={
          _.get(routes, 'show')
            ? `${_.get(routes, 'show')}/:entityID`
            : `${webPath}/:entityID`
        }
        component={views.show}
      />
    ),
    _.has(views, 'edit') && (
      <Route
        key={'edit'}
        name={routeName(localeSelector, 'edit')}
        staticName
        path={
          _.get(routes, 'edit')
            ? `${_.get(routes, 'edit')}/:entityID/edit`
            : `${webPath}/:entityID/edit`
        }
        component={views.edit}
      />
    ),
    _.has(views, 'review') && (
      <Route
        key={'review'}
        name={routeName(localeSelector, 'review')}
        staticName
        path={
          _.get(routes, 'review')
            ? `${_.get(routes, 'review')}/:entityID/review`
            : `${webPath}/:entityID/review`
        }
        component={views.review}
      />
    ),
    _.has(views, 'metrics') && (
      <Route
        key={'metrics'}
        name={routeName(localeSelector, 'metrics')}
        staticName
        path={
          _.get(routes, 'metrics')
            ? `${_.get(routes, 'metrics')}/:entityID/metrics`
            : `${webPath}/:entityID/metrics`
        }
        component={views.metrics}
      />
    ),
  ];

  const v2Routes = _.map(
    _.difference(_.keys(views), v1ViewsKeys),
    k =>
      _.has(views, k) && (
        <Route
          key={k}
          name={'Holos'}
          staticName
          path={_.get(routes, k) || `${webPath}/:entityID/${k}`}
          component={_.get(views, k)}
        />
      ),
  );

  return [...v1Routes, ...v2Routes];
}

const RtcAppContainer = (props: any) => (
  <AppContainer {...props} namespace={RTC_NAMESPACE} />
);
const RtcNotFoundPage = (props: any) => (
  <NotFoundPage {...props} namespace={RTC_NAMESPACE} />
);

export default function routes() {
  const modelConfigRoutes = Object.values(modelsConfig)
    .map(injectModelPropsToView)
    .map(getRoutes)
    .flat();

  return (
    <Route path="/" component={RtcAppContainer}>
      {/* Pages that will redirect to index if logged in */}
      <Route path="login" component={LoginPage} />
      <IndexRoute component={LoginPage} />
      <Route component={AuthorizedUserContainer}>
        <Route path="home" component={WelcomePage} />
        {modelConfigRoutes}
        <Route
          name={'View Trident Campaign'}
          path="/campaigns/:countryID/:entityID/view"
          staticName
        >
          <IndexRoute component={TridentCampaignViewPage} />
        </Route>
        <Route
          name={'Manage Event Condition Action'}
          path="/campaigns/manage-event-condition-action"
          staticName
        >
          <IndexRoute component={ManageEventConditionActionPage} />
        </Route>
        <Route
          name={'Holos'}
          path="/ucd/projects/:entityID/view"
          staticName
        >
          <IndexRoute component={ProjectDetailPage} />
        </Route>
        <Route
          name={'Debugging Tools'}
          path="/debugging-tools"
          staticName
        >
          <IndexRoute component={DebuggingToolsPage} />
        </Route>
        <Route
          name={'Content Generation Config'}
          path="/ucd/content-generation-config"
          staticName
        >
          <IndexRoute component={ContentGenerationConfigStandalonePage} />
        </Route>
        {/* @Plop: MODEL_ROUTES */}
        <Route path="*" component={RtcNotFoundPage} />
      </Route>
    </Route>
  );
}
